import React, { useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { PiCaretDoubleLeftBold, PiCaretRightBold } from "react-icons/pi";
import "./nocturne.css";
import "./admin.css";
import AiCopilotDrawer from "./components/AiCopilotDrawer";

/**
 * Admin shell — sidebar, topbar and the routed content well.
 *
 * Deliberately outside MainLayout: the existing layout renders the
 * construction app's own light chrome and sidebar, and this design brings its
 * own. `.nocturne` is applied here and nowhere else, which is what keeps the
 * dark design system from leaking into the rest of the app.
 */

export const NAV_SECTIONS = [
  { id: "dashboard", path: "/", label: "Dashboard", subtitle: "Platform health at a glance" },
  { id: "users", path: "/users", label: "Users", subtitle: "Homeowners, tradespeople and companies on the platform" },
  { id: "payments", path: "/payments", label: "Payments", subtitle: "Stripe transactions, entitlements and disputes" },
  { id: "requests", path: "/requests", label: "Service Requests", subtitle: "Projects posted by homeowners, from draft to completion" },
  { id: "tickets", path: "/tickets", label: "Tickets", subtitle: "Support queue with automated triage" },
  { id: "taxonomy", path: "/taxonomy", label: "Taxonomy", subtitle: "Reference data driving automated decisions" },
  { id: "media", path: "/media", label: "Category Media", subtitle: "Default images and videos shown per project type" },
  { id: "access", path: "/access", label: "Admin Access", subtitle: "Staff accounts, roles and invitations" },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [navExpanded, setNavExpanded] = useState(true);
  const [globalSearch, setGlobalSearch] = useState("");
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);

  // Longest matching path wins, so /users doesn't resolve to /admin.
  const active = useMemo(() => {
    const matches = NAV_SECTIONS.filter(
      (s) => location.pathname === s.path || location.pathname.startsWith(s.path + "/")
    );
    return matches.sort((a, b) => b.path.length - a.path.length)[0] || NAV_SECTIONS[0];
  }, [location.pathname]);

  return (
    <div className="nocturne admin-root">
      <nav className={`admin-nav ${navExpanded ? "admin-nav--expanded" : "admin-nav--collapsed"}`}>
        <div className="admin-brand">
          <div className="admin-brand-mark">B</div>
          {navExpanded && (
            <div>
              <div className="admin-brand-name">BuildBeta</div>
              <div className="admin-brand-sub">Admin</div>
            </div>
          )}
        </div>

        {NAV_SECTIONS.map((section) => {
          const isActive = section.id === active.id;
          return (
            <button
              key={section.id}
              type="button"
              title={navExpanded ? undefined : section.label}
              className={`admin-nav-item ${isActive ? "admin-nav-item--active" : ""}`}
              onClick={() => navigate(section.path)}
            >
              <span className="admin-nav-dot" />
              {navExpanded && <span className="admin-nav-label">{section.label}</span>}
            </button>
          );
        })}

        <div className="admin-nav-spacer" />

        <button
          type="button"
          className="admin-nav-toggle"
          onClick={() => setNavExpanded((v) => !v)}
          aria-label={navExpanded ? "Collapse navigation" : "Expand navigation"}
        >
          {navExpanded ? <PiCaretDoubleLeftBold /> : <PiCaretRightBold />}
          {navExpanded && <span>Collapse</span>}
        </button>
      </nav>

      <div className="admin-main">
        <header className="admin-topbar">
          <div>
            <h4 style={{ margin: 0 }}>{active.label}</h4>
            <div className="admin-topbar-sub">{active.subtitle}</div>
          </div>

          <div className="admin-spacer" />

          <input
            className="input admin-search"
            placeholder={`Search ${active.label.toLowerCase()}…`}
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
          />

          <button type="button" className="btn btn-secondary admin-alert-btn" title="Notifications">
            Alerts
            <span className="admin-alert-dot" />
          </button>

          <button type="button" className="btn btn-primary" onClick={() => setAiDrawerOpen(true)}>
            AI Copilot
          </button>

          <div className="admin-avatar" title="Olivia Ashcroft">OA</div>
        </header>

        <main className="admin-content">
          {/* Sections read the shared search term off the outlet context rather
              than each owning a duplicate search box. */}
          <Outlet context={{ search: globalSearch.trim().toLowerCase() }} />
        </main>
      </div>

      {aiDrawerOpen && <AiCopilotDrawer onClose={() => setAiDrawerOpen(false)} />}
    </div>
  );
};

export default AdminLayout;
